/**
 * CreditBalance
 *
 * A small, reusable chip/badge that shows the user's credit balance.
 * Clicking it opens the PaymentDialog to purchase more.
 *
 * Props:
 *   balance        {number|null}  — current balance (null = loading)
 *   lowThreshold   {number}       — warn below this (default 5)
 *   onClick        {fn}           — called when user clicks to top up
 *   size           {'small'|'medium'}
 *   unitLabel      {string}       — label for credits (default 'credits')
 */
import React from 'react';
import { Chip, Skeleton, Tooltip } from '@mui/material';
import {
  Bolt as BoltIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

const CreditBalance = ({
  balance,
  isUnlimited = false,
  lowThreshold = 5,
  onClick,
  size = 'small',
  unitLabel = 'credits',
}) => {
  if (isUnlimited) {
    return (
      <Tooltip title="Superuser — unlimited AI credits" arrow>
        <Chip
          icon={<BoltIcon />}
          label="∞ Unlimited"
          size={size}
          color="secondary"
          variant="outlined"
          sx={{ fontWeight: 600 }}
        />
      </Tooltip>
    );
  }

  if (balance === null) {
    return <Skeleton variant="rounded" width={80} height={24} />;
  }

  const isLow = balance <= lowThreshold;
  const isEmpty = balance === 0;

  const label = `${balance} ${unitLabel}`;
  const tip = isEmpty
    ? `No ${unitLabel} remaining — click to purchase`
    : isLow
    ? `Low balance: ${balance} ${unitLabel} remaining`
    : `${balance} ${unitLabel} available`;

  return (
    <Tooltip title={tip} arrow>
      <Chip
        icon={isEmpty || isLow ? <WarningIcon /> : <BoltIcon />}
        label={label}
        size={size}
        color={isEmpty ? 'error' : isLow ? 'warning' : 'default'}
        variant={isEmpty || isLow ? 'filled' : 'outlined'}
        onClick={onClick}
        clickable={!!onClick}
        sx={{ fontWeight: 600, cursor: onClick ? 'pointer' : 'default' }}
      />
    </Tooltip>
  );
};

export default CreditBalance;
